import { getTrackingAttributes } from '@rio-cloud/rio-uikit/googleAnalyticsUtils';
import RadioButton from '@rio-cloud/rio-uikit/components/radiobutton/RadioButton';
import { type FunctionComponent, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getTelematicsSelectionTrackingKey } from './trackingKeys';
import type { AssetTypeTile, BrandTile, TelematicsTile } from './configuration/types';

const isTranslationId = (value: string) => value.startsWith('assets.') || value.startsWith('intl-msg:');

interface TelematicsListProps {
    displayedTelematics: TelematicsTile[];
    selectedTelematic: TelematicsTile | undefined;
    selectedAssetType: AssetTypeTile | undefined;
    selectedBrand: BrandTile | undefined;
    setSelectedTelematic: (value: TelematicsTile | undefined) => void;
}

export const TelematicsList: FunctionComponent<TelematicsListProps> = ({
    displayedTelematics,
    selectedTelematic,
    selectedAssetType,
    selectedBrand,
    setSelectedTelematic,
}) => {
    const [selectedOption, setSelectedOption] = useState<string | undefined>(undefined);
    const [searchTerm, setSearchTerm] = useState('');
    const intl = useIntl();

    useEffect(() => {
        if (selectedTelematic) {
            setSelectedOption(selectedTelematic.displayName);
        } else {
            if (displayedTelematics.length > 0) {
                setSelectedTelematic(displayedTelematics[0]);
                setSelectedOption(displayedTelematics[0].displayName);
            }
        }
    }, [selectedTelematic, displayedTelematics, setSelectedTelematic]);

    const filteredTelematics = useMemo(() => {
        return displayedTelematics.filter(
            tile =>
                tile.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                intl.formatMessage({ id: tile.description }).toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [displayedTelematics, searchTerm, intl]);

    const getSelectedClass = (isSelected: boolean) =>
        isSelected ? 'border-color-highlight bg-highlight-decent z-index-1' : '';

    return (
        <div className='flex-basis-100pct'>
            <div className='form-group'>
                <div className='input-group'>
                    <span className='input-group-addon'>
                        <span className='rioglyph rioglyph-search' />
                    </span>
                    <input
                        type='text'
                        className='form-control'
                        placeholder='Find your data source'
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>
            <div className='panel panel-default max-height-450 overflow-x-hidden overflow-y-auto has-scrollbars rounded border-none'>
                {filteredTelematics.map((tile, index) => (
                    <RadioButton
                        key={tile.displayName}
                        name='stackedRadioBoxes'
                        custom
                        className={`padding-15 border ${
                            index === 0 ? 'rounded-top ' : ''
                        }${index === filteredTelematics.length - 1 ? 'rounded-bottom ' : ''}${getSelectedClass(
                            selectedOption === tile.displayName
                        )}`}
                        onChange={() => {
                            setSelectedOption(tile.displayName);
                            setSelectedTelematic(tile);
                        }}
                        checked={selectedOption === tile.displayName}
                        {...getTrackingAttributes({
                            trigger: 'click',
                            category: 'radio autoTracking',
                            action: 'radio click',
                            label: getTelematicsSelectionTrackingKey(selectedAssetType, selectedBrand, tile),
                        })}
                    >
                        <div className='radio-text-wrapper display-flex'>
                            <span className='radio-text' />
                            <div className='margin-left-5 margin-right-5'>
                                <div className='text-medium' data-testid={tile.displayName} data-cy={tile.displayName}>
                                    {isTranslationId(tile.displayName) ? (
                                        <FormattedMessage id={tile.displayName} />
                                    ) : (
                                        tile.displayName
                                    )}
                                </div>
                                <div className='text-color-dark'>
                                    <FormattedMessage id={tile.description} />
                                </div>
                            </div>
                        </div>
                    </RadioButton>
                ))}
            </div>
        </div>
    );
};
